import type { ClientOptions, TransactionEvent } from "@sentry/core";

/**
 * Drop entire transactions that are pure noise (middleware, static assets).
 * Middleware errors are still captured via onRequestError in each app's instrumentation.ts.
 */
export function shouldDropTransaction(event: TransactionEvent): boolean {
  const name = event.transaction ?? "";

  // Drop all middleware transactions (e.g. "middleware GET", "middleware POST")
  if (name.startsWith("middleware")) return true;

  // Drop static asset / non-app transactions
  if (
    name.startsWith("GET /_next/") ||
    name === "GET /sw.js" ||
    name === "GET /favicon.ico" ||
    name.endsWith(".svg") ||
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".ico") ||
    name.endsWith(".woff2")
  ) {
    return true;
  }

  return false;
}

/**
 * Entries for the SDK's top-level `ignoreSpans` option — the supported way to
 * drop individual spans since Sentry v9 (returning `null` from
 * `beforeSendSpan` no longer drops the span; the SDK logs a warning and keeps
 * it). Matching child spans are removed (their children are re-parented); a
 * match on the ROOT span drops the whole transaction, so every filter here is
 * anchored to avoid catching real app spans.
 *
 * String entries are substring matches in the SDK, so prefer RegExp.
 */
export type IgnoreSpans = NonNullable<ClientOptions["ignoreSpans"]>;

/**
 * Server/edge runtimes: sub-millisecond Next.js internals that add no
 * diagnostic value, plus outbound requests made by our own telemetry SDKs
 * (observability plumbing, not app work).
 */
export const SERVER_IGNORE_SPANS: IgnoreSpans = [
  // Next.js request-pipeline internals (NextNodeServer.startResponse,
  // findPageComponents, getLayoutOrPageModule).
  { name: /^start response$/ },
  { name: /^resolve page components$/ },
  { name: /^resolve segment modules$/ },
  // Outbound telemetry: PostHog server-side capture and Braintrust OTel
  // export. The AI/Delphi gateway calls are NOT matched — those are app work.
  { op: /^http\.client$/, name: /posthog\.com|api\.braintrust\.dev/ },
];

/**
 * Hosts of the third-party marketing/analytics scripts loaded via
 * `@medly/tracking-config` PixelScripts (GTM/Google Ads, Meta, TikTok,
 * Snapchat, Reddit) plus PostHog. Their fetch/XHR requests are already
 * excluded via `shouldCreateSpanForRequest`, but the scripts and tracking
 * beacons still generate `resource.*` spans on pageload/navigation traces.
 */
const THIRD_PARTY_RESOURCE_HOSTS =
  /googletagmanager\.com|google-analytics\.com|doubleclick\.net|connect\.facebook\.net|facebook\.com\/tr|analytics\.tiktok\.com|sc-static\.net|snapchat\.com|redditstatic\.com|alb\.reddit\.com|posthog\.com/;

/**
 * Browser runtime: resource spans for third-party pixel/analytics assets.
 * First-party resources (our _next/static bundles, fonts, images) are kept.
 */
export const CLIENT_IGNORE_SPANS: IgnoreSpans = [
  { op: /^resource\./, name: THIRD_PARTY_RESOURCE_HOSTS },
  // PostHog assets loaded through the same-origin /t reverse proxy
  // (e.g. /t/static/recorder.js).
  { op: /^resource\./, name: /\/t\/static\// },
];
